import React, { useEffect, useState } from "react";
import {
  containsHtml,
  safeUrl,
  sanitizeRichText,
} from "../../../helpers/sanitizeRichText";
import EnvelopeIcon from "./EnvelopeIcon";

export interface ThankYouHeaderProps {
  title: string;
  message?: string;
  note?: string;
  logoUrl?: string;
  logoAlt?: string;
  icon?: string;
  ready?: boolean;
  primaryColor?: string;
  titleClassName?: string;
  titleStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

const DEFAULT_TITLE_CLASS = "text-2xl font-bold text-gray-900 text-center mb-1";
const BRANDED_TITLE_CLASS = "font-bold text-gray-900 text-center mb-1";
const BRANDED_TITLE_STYLE: React.CSSProperties = {
  fontSize: "28px",
  lineHeight: 1.2,
};
const TEXT_CLASS = "text-sm text-gray-500 text-center";
const LOGO_MAX_HEIGHT = 64;
const RESOURCES_WAIT_MS = 3000;

export const THANK_YOU_LOGO_EXTRA_HEIGHT = 180;

export const THANK_YOU_ICON_EXTRA_HEIGHT = 80;

export const hasThankYouLogo = (logoUrl?: string): boolean =>
  !!safeUrl(logoUrl, { allowDataImage: true });

export const hasCustomThankYouIcon = (icon?: string): boolean =>
  !!safeUrl((icon || "").trim(), { allowDataImage: true });

export const getThankYouIframeHeight = (
  baseHeight: number,
  logoUrl?: string,
  icon?: string
): number => {
  if (hasThankYouLogo(logoUrl)) return baseHeight + THANK_YOU_LOGO_EXTRA_HEIGHT;
  if (hasCustomThankYouIcon(icon))
    return baseHeight + THANK_YOU_ICON_EXTRA_HEIGHT;
  return baseHeight;
};

const RichText: React.FC<{
  value: string;
  as: "h1" | "p";
  className: string;
  style?: React.CSSProperties;
}> = ({ value, as, className, style }) => {
  if (!containsHtml(value)) {
    return React.createElement(as, { className, style }, value);
  }
  const html = { __html: sanitizeRichText(value) };
  // <p> can't legally hold block children, so HTML messages get a <div> wrapper.
  return as === "h1" ? (
    <h1 className={className} style={style} dangerouslySetInnerHTML={html} />
  ) : (
    <div className={className} style={style} dangerouslySetInnerHTML={html} />
  );
};

// Keyed by src in the parent, so every new logo URL starts again from "loading".
const BrandLogo: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [status, setStatus] = useState<"loading" | "loaded" | "failed">(
    "loading"
  );
  // A logo that cannot be loaded is dropped instead of showing a broken image.
  if (status === "failed") return null;
  return (
    <div
      className="mb-4 flex justify-center"
      style={{
        maxWidth: "100%",
        minHeight: status === "loading" ? LOGO_MAX_HEIGHT : undefined,
      }}
    >
      <img
        src={src}
        alt={alt}
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("failed")}
        style={{
          maxWidth: "100%",
          maxHeight: LOGO_MAX_HEIGHT,
          objectFit: "contain",
          opacity: status === "loaded" ? 1 : 0,
        }}
      />
    </div>
  );
};

const ThankYouHeader: React.FC<ThankYouHeaderProps> = ({
  title,
  message,
  note,
  logoUrl,
  logoAlt,
  icon,
  ready = true,
  primaryColor,
  titleClassName,
  titleStyle,
  children,
}) => {
  const [gaveUpWaiting, setGaveUpWaiting] = useState(false);
  useEffect(() => {
    if (ready) {
      setGaveUpWaiting(false);
      return undefined;
    }
    const timer = setTimeout(() => setGaveUpWaiting(true), RESOURCES_WAIT_MS);
    return () => clearTimeout(timer);
  }, [ready]);
  const settingsKnown = ready || gaveUpWaiting;

  const color = primaryColor || "#22c55e";
  const logoSrc = settingsKnown
    ? safeUrl(logoUrl, { allowDataImage: true })
    : "";
  const iconSetting = settingsKnown ? (icon || "").trim() : "";
  const iconKey = iconSetting.toLowerCase();
  const hideIcon = iconKey === "none";
  const useCheckmark = iconKey === "checkmark";
  const customIconSrc =
    hideIcon || useCheckmark
      ? ""
      : safeUrl(iconSetting, { allowDataImage: true });
  // Envelope only when a logo is configured; otherwise the original checkmark is untouched.
  const useEnvelope = !!logoSrc && !hideIcon && !useCheckmark && !customIconSrc;
  const showCheckmark = !hideIcon && !customIconSrc && !useEnvelope;

  return (
    <div className="flex flex-col items-center pt-10 pb-6">
      {logoSrc && <BrandLogo key={logoSrc} src={logoSrc} alt={logoAlt || ""} />}
      {useEnvelope ? (
        <div className="mb-2">
          <EnvelopeIcon accentColor={color} />
        </div>
      ) : customIconSrc ? (
        <img
          src={customIconSrc}
          alt=""
          className="mb-4"
          style={{ maxWidth: "100%", maxHeight: 140, objectFit: "contain" }}
        />
      ) : (
        showCheckmark && (
          <div
            className={`rounded-full p-3 mb-4${
              settingsKnown ? "" : " invisible"
            }`}
            style={{ border: `2.5px solid ${color}` }}
            aria-hidden={settingsKnown ? undefined : true}
          >
            <svg
              style={{ width: 40, height: 40, color }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )
      )}
      <RichText
        value={title}
        as="h1"
        className={
          logoSrc ? BRANDED_TITLE_CLASS : titleClassName || DEFAULT_TITLE_CLASS
        }
        style={logoSrc ? BRANDED_TITLE_STYLE : titleStyle}
      />
      {message && <RichText value={message} as="p" className={TEXT_CLASS} />}
      {note && <RichText value={note} as="p" className={TEXT_CLASS} />}
      {children}
    </div>
  );
};

export default ThankYouHeader;
